import React, { useState } from 'react';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, CardContent, CardActions, TextField, Box, Grid, Typography, Modal, Stack, InputAdornment, Container, Fade, Zoom, Avatar, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Post, apiURL } from '../../../utils/apiCalls';
import OtpInput from 'react-otp-input';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 800,
  margin: '0 auto',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
}));

const ProcurementOnboardingSchema = Yup.object().shape({
  first_name: Yup.string().required('First Name is required'),
  last_name: Yup.string().required('Last Name is required'),
  email: Yup.string().email('Invalid Email').required('Email is required'),
  phone: Yup.string().matches(/^[6-9]\d{9}$/,'Please Enter valid Phone Number').required('Phone Number is required'),
  password: Yup.string().required('M-Pin is required').matches(/^[0-9]{4}$/, 'Invalid M-Pin')
});

const ProcurementOnboarding = () => {
  const { qr } = useParams();
  const navigate = useNavigate();
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpType, setOtpType] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: ''
    },
    validationSchema: ProcurementOnboardingSchema,
    onSubmit: async (values, { setSubmitting, setFieldError, setFieldTouched }) => {
      if (!isEmailVerified && !isPhoneVerified) {
        setFieldError('email', 'Please verify Email OTP');
        setFieldError('phone', 'Please verify Phone OTP');
        setFieldTouched('email', true);
        setFieldTouched('phone', true);
        setSubmitting(false);
        return;
      } else if (!isEmailVerified) {
        setFieldError('email', 'Please verify Email OTP');
        setFieldTouched('email', true);
        setSubmitting(false);
        return;
      } else if (!isPhoneVerified) {
        setFieldError('phone', 'Please verify Phone OTP');
        setFieldTouched('phone', true);
        setSubmitting(false);
        return;
      }

      try {
        // Create minimal procurement record; backend should accept minimal payload with role 3
        const response = await Post(apiURL + '/api/v1/users/create/admin/without_auth', { ...values, qr, role: 3 });
        if (response && response.data && response.data.status) {
          toast.success('Registration successful!');
          setIsSubmitted(true);
        } else {
          toast.error(response?.data?.message || 'Please Try Again');
        }
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { values, errors, touched, getFieldProps, isSubmitting, setFieldError, setFieldValue } = formik;

  const handleOtpOpen = async (type) => {
    const entity = type === 'email' ? values.email : values.phone;
    if (!entity) {
      toast.error(type === 'email' ? 'Enter email first' : 'Enter phone first');
      return;
    }
    const response = await Post(apiURL + `/api/v1/users/registration/_sent_otp_registration`, { entity });
    if (response && response.data && response.data.status) {
      toast.success('OTP sent successfully');
      setOtpType(type);
      setOtpModalOpen(true);
    } else {
      toast.error(response?.data?.message || 'Failed to send OTP');
    }
  };

  const handleOtpClose = () => {
    setOtpModalOpen(false);
    setOtpValue('');
  };

  const handleOtpSubmit = async () => {
    const entity = otpType === 'email' ? values.email : values.phone;
    const response = await Post(apiURL + `/api/v1/users/registration/_verify_otp_registration`, { entity, otp: otpValue });
    if (response && response.data && response.data.status) {
      toast.success('OTP Verified Successfully');
      setOtpModalOpen(false);
      setOtpValue('');
      if (otpType === 'email') {
        setIsEmailVerified(true);
        setFieldError('email', '');
      } else {
        setIsPhoneVerified(true);
        setFieldError('phone', '');
      }
    } else {
      toast.error('Invalid OTP. Please try again.');
    }
  };

  if (isSubmitted) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Fade in timeout={800}>
          <StyledCard sx={{ textAlign: 'center', p: 4, borderRadius: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Zoom in timeout={1000}>
                  <Avatar
                    sx={{
                      bgcolor: '#4caf50',
                      width: 80,
                      height: 80,
                      boxShadow: '0 8px 24px rgba(76, 175, 80, 0.4)',
                      margin: '0 auto'
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 50, color: 'white' }} />
                  </Avatar>
                </Zoom>
              </Box>
              <Typography variant="h3" component="h1" fontWeight="bold" color="text.primary" gutterBottom sx={{ mb: 2 }}>
                Request Sent Successfully!
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto', fontWeight: 'normal' }}>
                Your procurement partner onboarding request has been submitted. Please wait for administrator approval.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
              >
                GO TO LOGIN
              </Button>
            </CardContent>
          </StyledCard>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
          <StyledCard>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>Procurement Partner Onboarding</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Enter basic details and verify your email and phone. After verification, login to pay subscription and complete your full profile.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    {...getFieldProps('first_name')}
                    error={Boolean(touched.first_name && errors.first_name)}
                    helperText={touched.first_name && errors.first_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    {...getFieldProps('last_name')}
                    error={Boolean(touched.last_name && errors.last_name)}
                    helperText={touched.last_name && errors.last_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    {...getFieldProps('email')}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isEmailVerified ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <Button variant="outlined" size="small" onClick={() => handleOtpOpen('email')}>Verify</Button>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    {...getFieldProps('phone')}
                    error={Boolean(touched.phone && errors.phone)}
                    helperText={touched.phone && errors.phone}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isPhoneVerified ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <Button variant="outlined" size="small" onClick={() => handleOtpOpen('phone')}>Verify</Button>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Set Mpin</Typography>
                  <OtpInput
                    numInputs={4}
                    isInputNum
                    onChange={(otp) => setFieldValue('password', otp)}
                    value={values.password}
                    containerStyle={{ gap: '10px', display: 'flex', alignItems: 'center' }}
                    inputStyle={{
                      color: '#001B71',
                      '-webkit-text-fill-color': 'black !important',
                      padding: '0px',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderRadius: '4px',
                      boxSizing: 'content-box',
                      height: '50px',
                      backgroundColor: '#BDCEE9',
                      display: 'block',
                      width: '50px',
                      minWidth: '0px',
                      border: '2px solid #BDCEE9'
                    }}
                    renderInput={(props) => <input type="password" {...props} />}
                  />
                  <FormHelperText error={Boolean(touched.password && errors.password)}>
                    {(touched.password && errors.password) || 'Enter 4 digit Mpin'}
                  </FormHelperText>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ display: 'flex', justifyContent: 'space-between', px: 4, pb: 4 }}>
              <Button variant="outlined" onClick={() => navigate('/login')}>Back</Button>
              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>Request</LoadingButton>
            </CardActions>
          </StyledCard>

          <Modal open={otpModalOpen} onClose={handleOtpClose}>
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 4 }}>
              <Typography variant="h6" align="center" sx={{ mb: 2 }}>
                Enter OTP for {otpType === 'email' ? 'Email' : 'Phone'}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <OtpInput
                  numInputs={4}
                  isInputNum
                  onChange={(otp) => setOtpValue(otp)}
                  value={otpValue}
                  containerStyle={{ gap: '10px', display: 'flex', alignItems: 'center' }}
                  inputStyle={{
                    color: '#001B71',
                    '-webkit-text-fill-color': 'black !important',
                    padding: '0px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    borderRadius: '4px',
                    boxSizing: 'content-box',
                    height: '50px',
                    backgroundColor: '#BDCEE9',
                    display: 'block',
                    width: '50px',
                    minWidth: '0px',
                    border: '2px solid #BDCEE9'
                  }}
                  renderInput={(props) => <input type="text" {...props} />}
                />
              </Box>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="outlined" onClick={handleOtpClose} sx={{ minWidth: 100 }}>Cancel</Button>
                <Button variant="contained" onClick={handleOtpSubmit} disabled={otpValue.length !== 4} sx={{ minWidth: 100 }}>Verify OTP</Button>
              </Stack>
            </Box>
          </Modal>
        </Form>
      </FormikProvider>
    </Container>
  );
};

export default ProcurementOnboarding;


